// "Channels you might love" — horizontal rail on Home featuring
// creators the user hasn't subscribed to yet. Pulls from
// /api/channel/suggestions which ranks by public-post count.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, BellSimple, ArrowRight } from "@phosphor-icons/react";
import { listChannelSuggestions, subscribeTo } from "../lib/channels";
import { useAuth } from "../context/AuthContext";

export default function ChannelSuggestionsRail() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    listChannelSuggestions(8).then((rows) => setChannels(rows || [])).catch(() => setChannels([]));
  }, [user]);

  const handleSubscribe = async (cid) => {
    try {
      const updated = await subscribeTo(cid);
      setChannels((arr) => arr.map((c) => (c.user_id === cid ? updated : c)));
    } catch (_) {}
  };

  if (!channels.length) return null;

  return (
    <section className="mt-10 md:mt-14" data-testid="home-channel-suggestions">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-terracotta flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Find your tribe
          </p>
          <h2 className="font-heading text-2xl md:text-3xl text-forest mt-1.5 leading-tight">
            Channels you might love
          </h2>
        </div>
        <Link to="/foryou" className="text-xs text-terracotta hover:underline inline-flex items-center gap-1" data-testid="channel-suggestions-see-all">
          Explore all <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
      <div
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 md:mx-0 md:px-0"
        style={{ scrollbarWidth: "thin" }}
        data-testid="channel-suggestions-rail"
      >
        {channels.map((c) => (
          <div
            key={c.user_id}
            className="snap-start shrink-0 w-52 md:w-60 rounded-2xl bg-white border border-cream-300 overflow-hidden group hover:border-terracotta transition-all"
            data-testid={`channel-suggestion-${c.user_id}`}
          >
            <Link to={`/u/${c.user_id}`} className="block">
              <div className="aspect-[3/1] relative overflow-hidden bg-ink">
                {(() => {
                  const raw = c.avatar_url || "";
                  const avatar = raw && !raw.startsWith("http")
                    ? `${process.env.REACT_APP_BACKEND_URL}${raw}`
                    : raw;
                  const seed = Math.abs([...(c.name || "Holy")].reduce((a, ch) => a + ch.charCodeAt(0), 0));
                  const stockFace = `https://i.pravatar.cc/640?img=${(seed % 70) + 1}`;
                  const src = c.banner_url || avatar || stockFace;
                  return (
                    <img
                      src={src}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => {
                        if (!e.currentTarget.dataset.swapped) {
                          e.currentTarget.dataset.swapped = "1";
                          e.currentTarget.src = stockFace;
                        }
                      }}
                    />
                  );
                })()}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
            </Link>
            <div className="p-3">
              <Link to={`/u/${c.user_id}`} className="block">
                <p className="font-semibold text-ink truncate">{c.name}</p>
                {c.tagline && <p className="text-[11px] text-muted_ink line-clamp-2 mt-0.5">{c.tagline}</p>}
                <p className="text-[10px] text-muted_ink mt-1">{c.subscriber_count} subs · {c.video_count} videos</p>
              </Link>
              {user && !c.is_self && (
                <button
                  onClick={() => handleSubscribe(c.user_id)}
                  disabled={c.is_subscribed_by_me}
                  data-testid={`channel-suggestion-subscribe-${c.user_id}`}
                  className={`w-full mt-3 h-9 rounded-full text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${
                    c.is_subscribed_by_me ? "bg-cream-200 text-forest" : "bg-forest text-cream hover:bg-forest-600"
                  }`}
                >
                  <BellSimple size={12} weight="fill" />
                  {c.is_subscribed_by_me ? "Subscribed" : "Subscribe"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
